import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Radio } from 'lucide-react';
import LiveCard from '../LiveCard';

/**
 * CategoriesTab — 🏷 Categorie del profilo Voice-First.
 * ONLY existing data: user's macro-category (admin taxonomy) + topics +
 * the person's Lives (which carry their own existing category string).
 * NO new content-category associations are invented: Racconti have no
 * category in the data model, so they are never filtered here.
 */
const CategoriesTab = ({ username, categorySlug, categoryName, secondaryCategories = [], topics = [], lives = [] }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);

  const chips = [];
  if (categoryName) chips.push({ id: 'macro', label: categoryName, slug: categorySlug, isMacro: true });
  // F9.6.2 — Voce Pro secondary categories (same admin taxonomy).
  secondaryCategories.forEach((c) => chips.push({ id: `sec-${c.slug}`, label: c.name, slug: c.slug, isMacro: true }));
  topics.forEach((tp) => chips.push({ id: `topic-${tp}`, label: tp, isMacro: false }));

  if (chips.length === 0) {
    return (
      <p className="rounded-xl border border-border bg-card px-4 py-6 text-center text-xs text-muted-foreground" data-testid="categories-empty">
        @{username} non ha ancora indicato categorie o argomenti.
      </p>
    );
  }

  const matchingLives = selected
    ? lives.filter((l) => {
      const cat = (l.category || '').trim().toLowerCase();
      // New lives store the slug; legacy lives store free text — match both.
      return cat === (selected.slug || '').toLowerCase() || cat === selected.label.trim().toLowerCase();
    })
    : [];

  return (
    <div data-testid="categories-tab">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
        Di cosa parla @{username}
      </p>
      <div className="flex flex-wrap gap-1.5" data-testid="categories-chips">
        {chips.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setSelected(selected?.id === c.id ? null : c)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
              selected?.id === c.id
                ? 'border-amber-500 bg-amber-500/15 gold-text'
                : c.isMacro
                  ? 'border-amber-500/40 bg-amber-500/5 gold-text hover:bg-amber-500/10'
                  : 'border-border text-muted-foreground hover:bg-secondary'
            }`}
            data-testid={`category-chip-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {selected && (
        <div className="mt-4" data-testid="category-selected-content">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-red-500">
            <Radio className="h-3.5 w-3.5" /> Live in «{selected.label}»
          </p>
          {matchingLives.length > 0 ? (
            <div className="flex flex-col gap-2" data-testid="category-lives-list">
              {matchingLives.map((l) => <LiveCard key={l.id} live={l} compact />)}
            </div>
          ) : (
            <p className="rounded-xl border border-border bg-card px-4 py-4 text-center text-xs text-muted-foreground" data-testid="category-lives-empty">
              Nessuna Live di @{username} in questa categoria al momento.
            </p>
          )}
        </div>
      )}

      {categorySlug && (
        <button
          type="button"
          onClick={() => navigate(`/explore?category=${encodeURIComponent(categorySlug)}`)}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm font-semibold gold-text transition-colors hover:bg-amber-500/10"
          data-testid="category-explore-link"
        >
          <Search className="h-4 w-4" /> Trova altre persone in {categoryName}
        </button>
      )}
    </div>
  );
};

export default CategoriesTab;
